import { Dispatch } from '@reduxjs/toolkit';
import axios, { AxiosInstance, AxiosResponse, AxiosError, AxiosRequestConfig } from 'axios';

import { notify } from 'components/Common/Toast';
import { authURL } from 'config/apiConfig';
import { resetState } from 'redux/action/resetState';
import { updateAuthInfo } from 'redux/slices/auth/authSlice';

export interface ErrorResponse extends AxiosRequestConfig {
    _retry?: boolean;
}

interface ExtendError extends AxiosError {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing
    response: any;
}

// Mirrors the backend token pair (src/auth/auth.service.ts) and authSlice's AuthData:
// expiresIn is always present, so it is not optional here.
interface TokenPair {
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
}

// The refresh token is an opaque random string on the new backend (only its hash is stored),
// not a JWT, so it cannot be inspected client-side - calling the endpoint is the only way to
// know whether it is still valid. It is also single-use: the backend rotates it on every
// refresh and treats a replay as theft, revoking every token in the family. Parallel 401s
// must therefore share ONE refresh call, and that has to hold across both client instances -
// hence module scope rather than instance scope.
let refreshInFlight: Promise<TokenPair | null> | null = null;

// A bare axios call on purpose: no interceptors to recurse into, no Authorization header (the
// expired one is pointless here) and none of the legacy api-key/tenantName headers.
const postRefresh = async (refreshToken: string): Promise<TokenPair> => {
    const { data } = await axios.post<TokenPair>(
        `${authURL}/auth/refresh`,
        { refreshToken },
        { headers: { 'Content-Type': 'application/json' } }
    );
    return data;
};

class ApiClient {
    private client: AxiosInstance;
    private token: string | undefined;
    private refreshToken: string | undefined;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type -- pre-existing
    private dispatch: Function | undefined;

    // eslint-disable-next-line @typescript-eslint/no-empty-object-type -- pre-existing
    constructor(baseURL: string, headers?: {}) {
        this.client = axios.create({
            baseURL,
            headers: {
                'Content-Type': 'application/json',
                ...headers,
            },
        });
        this.handleRequest = this.handleRequest.bind(this);
        this.handleRequestError = this.handleRequestError.bind(this);
        this.handleSuccessResponse = this.handleSuccessResponse.bind(this);
        this.handleErrorResponse = this.handleErrorResponse.bind(this);
        this.refreshAccessToken = this.refreshAccessToken.bind(this);
        this.setDispatch = this.setDispatch.bind(this);
        this.clearTokens = this.clearTokens.bind(this);

        // Interceptors to handle request, response, and errors
        this.client.interceptors.request.use(this.handleRequest, this.handleRequestError);

        this.client.interceptors.response.use(this.handleSuccessResponse, this.handleErrorResponse);
    }

    clearTokens() {
        this.token = undefined;
        this.refreshToken = undefined;
        localStorage.removeItem('token');
        localStorage.removeItem('userAttempts');
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing
    private handleRequest(config: any) {
        if (this?.token) {
            // Set token in the Authorization header
            config.headers.Authorization = `Bearer ${this.token}`;
        }
        // FormData must not keep the instance JSON Content-Type — browser/axios set the boundary.
        if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
            if (config.headers?.delete) {
                config.headers.delete('Content-Type');
            } else if (config.headers) {
                delete config.headers['Content-Type'];
                delete config.headers['content-type'];
            }
        }
        return config;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing
    private handleRequestError(error: any) {
        return Promise.reject(error);
    }

    private handleSuccessResponse(response: AxiosResponse) {
        // You can customize the success response handling here
        return response.data;
    }

    private async handleErrorResponse(error: ExtendError) {
        if (error?.response) {
            const originalRequest = error.config as ErrorResponse | undefined;
            if (!originalRequest) {
                return Promise.reject(error);
            }
            const { Authorization, authorization } = originalRequest.headers || {};
            const _resMessage = error?.response?.data?.message || '';
            if (
                (Authorization || authorization) &&
                (error.response.status === 401 || _resMessage.includes('401')) &&
                !originalRequest._retry
            ) {
                originalRequest._retry = true;
                const res = await this.refreshAccessToken();
                if (!res) {
                    return Promise.reject(error);
                }
                this.client.defaults.headers.common['Authorization'] = `Bearer ${res.accessToken}`;
                // The failed request already carries the stale Authorization header, and a
                // default header does not override a per-request one - so overwrite it here or
                // the retry 401s again with the token we just replaced.
                if (originalRequest.headers) {
                    originalRequest.headers.Authorization = `Bearer ${res.accessToken}`;
                }
                return this.client.request(originalRequest);
            }
            return Promise.reject(error);
        } else if (error.request) {
            // Never log the raw request/XHR object here — see handleErrorMessage in
            // createAsyncThunkWrapper.ts for the same rule applied to the response side.
            if (process.env.NODE_ENV !== 'production') {
                console.error('Request made but no response received:', error.message);
            }
        } else {
            // Something happened in setting up the request that triggered an Error
            console.error('Error setting up the request:', error.message);
        }

        // You can throw a custom error or handle it as needed
        throw error;
    }

    // Set the token dynamically
    setToken(token: string | undefined, refreshToken?: string | undefined) {
        this.token = token || this.token;
        this.refreshToken = refreshToken || this.refreshToken;
    }

    private async refreshAccessToken(): Promise<TokenPair | null> {
        if (!this?.refreshToken) {
            throw new Error('Refresh token is required');
        }
        if (!refreshInFlight) {
            const token = this.refreshToken;
            refreshInFlight = postRefresh(token)
                // eslint-disable-next-line @typescript-eslint/no-explicit-any -- matches this file's other untyped catch/config handlers
                .catch((error: any) => {
                    // postRefresh sends the refresh token in the request body — logging the raw
                    // error would print it via error.config.data. Log only the response body.
                    if (process.env.NODE_ENV !== 'production') {
                        console.error('Error refreshing token:', error?.response?.data ?? error?.message);
                    }
                    return null;
                })
                .finally(() => {
                    refreshInFlight = null;
                });
        }
        const tokens = await refreshInFlight;
        if (!tokens?.accessToken) {
            this.endSession();
            return null;
        }
        this.setToken(tokens.accessToken, tokens.refreshToken);
        // Refresh rotates the refresh token; sync the Nest client immediately so
        // retries never reuse the dead token (theft revoke).
        syncClientTokens(tokens.accessToken, tokens.refreshToken);
        // Push the rotated pair into redux so ProtectedRoute stays aligned.
        // The previous refresh token is dead the moment this call succeeds.
        if (this.dispatch) {
            this.dispatch(updateAuthInfo(tokens));
        }
        return tokens;
    }

    private endSession() {
        notify('Session expired, Please login again.', 'warning');
        this.clearTokens();
        if (this.dispatch) {
            this.dispatch(resetState());
        }
    }
    setDispatch(dispatch: Dispatch) {
        this.dispatch = dispatch;
    }
    // Interceptors unwrap `response.data`, so callers receive the body (not AxiosResponse).
    async get<T = unknown>(endpoint: string, options?: AxiosRequestConfig): Promise<T> {
        const response = await this.client.get<T>(endpoint, options);
        return response as T;
    }

    async post<T = unknown>(
        endpoint: string,
        data?: unknown,
        // Callers pass HTTP headers and occasional client flags (e.g. hideToast).
        headers?: Record<string, unknown>
    ): Promise<T> {
        const response = await this.client.post<T>(endpoint, data, {
            headers: headers as AxiosRequestConfig['headers'],
        });
        return response as T;
    }

    async delete<T = unknown>(endpoint: string, data?: unknown): Promise<T> {
        return (await this.client.delete<T>(endpoint, data as AxiosRequestConfig)) as T;
    }

    async put<T = unknown>(endpoint: string, data?: unknown, headers?: Record<string, string>): Promise<T> {
        return (await this.client.put<T>(endpoint, data, {
            headers: this.headersForBody(data, headers),
        })) as T;
    }

    async patch<T = unknown>(endpoint: string, data?: unknown, headers?: Record<string, string>): Promise<T> {
        return (await this.client.patch<T>(endpoint, data, {
            headers: this.headersForBody(data, headers),
        })) as T;
    }

    /** FormData: never set Content-Type manually — runtime must add the multipart boundary. */
    private headersForBody(data: unknown, headers?: Record<string, string>): Record<string, string> | undefined {
        if (typeof FormData !== 'undefined' && data instanceof FormData) {
            if (!headers) return undefined;
            const { 'Content-Type': _, ...rest } = headers;
            void _;
            return rest;
        }
        return headers;
    }
}
// Phase 12: Nest `/v1` only — Bearer JWT, no api-key/tenantName, no legacy Lift client.
export const authClient = new ApiClient(authURL);
/** Prefer this name for non-auth /v1 calls (same instance as authClient). */
export const appClient = authClient;

function syncClientTokens(accessToken: string, refreshToken: string) {
    authClient.setToken(accessToken, refreshToken);
}
